import React, { useState, useEffect } from "react";
import { Form, Button } from "react-bootstrap";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import { useTranslation } from "react-i18next";

/**
 * Componente de paginación reutilizable para listas de items
 *
 * @param {Array} items - Array de items a paginar
 * @param {Array} itemsPerPageOptions - Opciones de items por página (default: [5, 10, 15, 20])
 * @param {Number} defaultItemsPerPage - Items por página por defecto (default: 10)
 * @param {Function} renderItem - Función para renderizar cada item
 * @param {String} itemLabel - Label traducible para items (default: "items")
 * @param {String} containerClassName - Clase CSS para el contenedor de items
 * @param {Object} containerStyle - Estilos inline para el contenedor de items
 */
const ListPagination = ({
  items = [],
  itemsPerPageOptions = [5, 10, 15, 20],
  defaultItemsPerPage = 10,
  renderItem,
  itemLabel = "items",
  containerClassName = "",
  containerStyle = {},
}) => {
  const { t } = useTranslation();

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(defaultItemsPerPage);

  // Calcular totales
  const totalItems = items.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems);
  const currentItems = items.slice(startIndex, endIndex);

  // Reset a página 1 cuando cambian los items o items por página
  useEffect(() => {
    setCurrentPage(1);
  }, [items.length, itemsPerPage]);

  // Handlers
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      // Scroll suave al inicio de la lista
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value, 10));
  };

  // Generar números de página
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      // Mostrar todas las páginas
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Mostrar páginas con ellipsis
      if (currentPage <= 3) {
        pages.push(1, 2, 3, 4, "...", totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1, "...", totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages);
      }
    }

    return pages;
  };

  // Si no hay items, no mostrar nada
  if (totalItems === 0) {
    return null;
  }

  return (
    <>
      {/* Información y selector de items por página */}
      <div
        className="d-flex justify-content-between align-items-center mb-3"
        style={{
          padding: "12px 16px",
          borderRadius: "12px",
          background: "rgba(255,255,255,0.1)",
          backdropFilter: "blur(10px)",
        }}
      >
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <span className="text-white" style={{ fontSize: "0.9rem" }}>
            {t("pagination.showing")} {startIndex + 1} {t("pagination.to")} {endIndex} {t("pagination.of")} {totalItems} {t(itemLabel)}
          </span>
        </div>

        <div className="d-flex align-items-center gap-2">
          <span className="text-white" style={{ fontSize: "0.85rem" }}>
            {t("pagination.itemsPerPage")}:
          </span>
          <Form.Select
            size="sm"
            value={itemsPerPage}
            onChange={handleItemsPerPageChange}
            style={{
              width: "80px",
              borderRadius: "8px",
              background: "rgba(255,255,255,0.9)",
              border: "none",
              fontSize: "0.85rem",
            }}
          >
            {itemsPerPageOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </Form.Select>
        </div>
      </div>

      {/* Renderizar items */}
      <div className={containerClassName} style={containerStyle}>
        {currentItems.map((item, index) => renderItem(item, startIndex + index))}
      </div>

      {/* Controles de paginación */}
      {totalPages > 1 && (
        <div
          className="d-flex justify-content-center align-items-center gap-2 mt-4 flex-wrap"
          style={{
            padding: "16px",
            borderRadius: "12px",
            background: "rgba(255,255,255,0.1)",
            backdropFilter: "blur(10px)",
          }}
        >
          {/* Primera página */}
          <Button
            size="sm"
            onClick={() => handlePageChange(1)}
            disabled={currentPage === 1}
            style={{
              borderRadius: "8px",
              background: currentPage === 1 ? "rgba(255,255,255,0.2)" : "rgba(255,255,255,0.9)",
              border: "none",
              color: currentPage === 1 ? "rgba(255,255,255,0.5)" : "#333",
              padding: "6px 10px",
              minWidth: "36px",
              cursor: currentPage === 1 ? "not-allowed" : "pointer",
            }}
            title={t("pagination.first")}
          >
            <ChevronsLeft size={16} />
          </Button>

          {/* Página anterior */}
          <Button
            size="sm"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            style={{
              borderRadius: "8px",
              background: currentPage === 1 ? "rgba(255,255,255,0.2)" : "rgba(255,255,255,0.9)",
              border: "none",
              color: currentPage === 1 ? "rgba(255,255,255,0.5)" : "#333",
              padding: "6px 10px",
              minWidth: "36px",
              cursor: currentPage === 1 ? "not-allowed" : "pointer",
            }}
            title={t("pagination.previous")}
          >
            <ChevronLeft size={16} />
          </Button>

          {/* Números de página */}
          {getPageNumbers().map((page, index) => {
            if (page === "...") {
              return (
                <span
                  key={`ellipsis-${index}`}
                  className="text-white px-2"
                  style={{ fontSize: "0.9rem" }}
                >
                  ...
                </span>
              );
            }

            return (
              <Button
                key={page}
                size="sm"
                onClick={() => handlePageChange(page)}
                style={{
                  borderRadius: "8px",
                  background: currentPage === page ? "#667eea" : "rgba(255,255,255,0.9)",
                  border: "none",
                  color: currentPage === page ? "white" : "#333",
                  padding: "6px 12px",
                  minWidth: "36px",
                  fontWeight: currentPage === page ? "600" : "400",
                }}
              >
                {page}
              </Button>
            );
          })}

          {/* Página siguiente */}
          <Button
            size="sm"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            style={{
              borderRadius: "8px",
              background: currentPage === totalPages ? "rgba(255,255,255,0.2)" : "rgba(255,255,255,0.9)",
              border: "none",
              color: currentPage === totalPages ? "rgba(255,255,255,0.5)" : "#333",
              padding: "6px 10px",
              minWidth: "36px",
              cursor: currentPage === totalPages ? "not-allowed" : "pointer",
            }}
            title={t("pagination.next")}
          >
            <ChevronRight size={16} />
          </Button>

          {/* Última página */}
          <Button
            size="sm"
            onClick={() => handlePageChange(totalPages)}
            disabled={currentPage === totalPages}
            style={{
              borderRadius: "8px",
              background: currentPage === totalPages ? "rgba(255,255,255,0.2)" : "rgba(255,255,255,0.9)",
              border: "none",
              color: currentPage === totalPages ? "rgba(255,255,255,0.5)" : "#333",
              padding: "6px 10px",
              minWidth: "36px",
              cursor: currentPage === totalPages ? "not-allowed" : "pointer",
            }}
            title={t("pagination.last")}
          >
            <ChevronsRight size={16} />
          </Button>
        </div>
      )}
    </>
  );
};

export default ListPagination;
